import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Shield, CheckCircle, XCircle, Clock, Store, 
  MapPin, Utensils, Users, Loader2, DollarSign, Star, Trash2,
  BarChart3, EyeOff, Eye, Megaphone, Power, TrendingUp, Mail, UserPlus, AlertTriangle
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/apiClient';
import { formatOrderNumberLabel } from '../lib/orderUtils';
import AdminProfitSection from '../components/admin/AdminProfitSection';
import DailyReportSettings from '../components/admin/DailyReportSettings';
import ComplaintsPanel from '../components/ComplaintsPanel';
import TruckLocationSettings from '../components/TruckLocationSettings';

const PAYOUT_PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [pendingTrucks, setPendingTrucks] = useState([]);
  const [allTrucks, setAllTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectingTruck, setRejectingTruck] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deletingTruck, setDeletingTruck] = useState(null);
  const [analytics, setAnalytics] = useState({
    total_trucks: 0,
    active_vendors: 0,
    total_revenue: 0,
    vendors: [],
    trucks: [],
  });
  const [insights, setInsights] = useState(null);
  const [viewType, setViewType] = useState('vendors');
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [walletSettings, setWalletSettings] = useState({
    wallet_bonus_percent: 0,
    wallet_topup_presets_cents: [2000, 5000, 10000],
  });
  const [walletSaving, setWalletSaving] = useState(false);
  const [presetInput, setPresetInput] = useState('20,50,100');
  const [supportSettings, setSupportSettings] = useState({
    support_email: 'support@skipdq.me',
    vendor_support_email: 'vendors@skipdq.ca',
  });
  const [supportSaving, setSupportSaving] = useState(false);
  const [feeSettings, setFeeSettings] = useState({
    fee_name: 'Student Builder Support Fee',
    convenience_fee_percent: 6,
    convenience_fee_min_amount: 1,
    convenience_fee_free_orders_count: 0,
    processing_percent: 2.9,
    referral_paused: false,
  });
  const [feeSaving, setFeeSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewTruckFilter, setReviewTruckFilter] = useState('all');
  const [deletingReview, setDeletingReview] = useState(null);
  const [suppressingReview, setSuppressingReview] = useState(null);
  const [evidenceOrderId, setEvidenceOrderId] = useState('');
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceData, setEvidenceData] = useState(null);
  const [adminComplaints, setAdminComplaints] = useState([]);
  const [complaintStatusFilter, setComplaintStatusFilter] = useState('all');
  const [complaintTruckFilter, setComplaintTruckFilter] = useState('all');
  const [payoutPeriod, setPayoutPeriod] = useState('day');
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutOrders, setPayoutOrders] = useState([]);
  const [commissionDrafts, setCommissionDrafts] = useState({});
  const [commissionSaving, setCommissionSaving] = useState(null);
  const [payoutEmailDrafts, setPayoutEmailDrafts] = useState({});
  const [payoutEmailSaving, setPayoutEmailSaving] = useState(null);
  const [staffTruck, setStaffTruck] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffAssigning, setStaffAssigning] = useState(false);
  const [staffRemoving, setStaffRemoving] = useState(null);
  const [locationTruck, setLocationTruck] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      setLoading(false);
      return undefined;
    }
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [user?.role, reviewTruckFilter, payoutPeriod]);

  const fetchPayoutData = async () => {
    try {
      const [summaryRes, ordersRes] = await Promise.all([
        apiFetch(`/api/admin/payout-summary?period=${payoutPeriod}`),
        apiFetch(`/api/admin/overview/orders?period=${payoutPeriod}`),
      ]);
      if (summaryRes.ok) setPayoutSummary(await summaryRes.json());
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const pickupMs = (row) => (row.scheduled_pickup_time ? new Date(row.scheduled_pickup_time).getTime() : Infinity);
        setPayoutOrders([...(data.orders || [])].sort((a, b) => pickupMs(a) - pickupMs(b)));
      }
    } catch (error) {
      console.error('Failed to fetch payout data:', error);
    }
  };

  const fetchData = async () => {
    try {
      const reviewUrl = reviewTruckFilter && reviewTruckFilter !== 'all'
        ? `/api/admin/reviews?truck_id=${encodeURIComponent(reviewTruckFilter)}`
        : '/api/admin/reviews';
      const [pendingRes, allRes, analyticsRes, walletRes, supportRes, feeRes, reviewsRes, insightsRes, complaintsRes] = await Promise.all([
        apiFetch('/api/admin/pending-trucks'),
        apiFetch('/api/admin/all-trucks'),
        apiFetch('/api/admin/overview'),
        apiFetch('/api/admin/wallet-settings'),
        apiFetch('/api/admin/support-settings'),
        apiFetch('/api/admin/fee-settings'),
        apiFetch(reviewUrl),
        apiFetch('/api/admin/overview/insights'),
        apiFetch('/api/admin/complaints'),
      ]);

      if (pendingRes.ok) {
        setPendingTrucks(await pendingRes.json());
      }
      if (allRes.ok) {
        const trucks = await allRes.json();
        setAllTrucks(trucks);
        const drafts = {};
        const emailDrafts = {};
        trucks.forEach((t) => {
          drafts[t.truck_id] = String(t.vendor_commission_percent ?? 5);
          emailDrafts[t.truck_id] = t.payout_email || '';
        });
        setCommissionDrafts(drafts);
        setPayoutEmailDrafts((prev) => {
          const next = { ...emailDrafts };
          // Keep unsaved edits across the 30s refresh.
          Object.keys(prev).forEach((id) => {
            const truck = trucks.find((t) => t.truck_id === id);
            if (truck && prev[id] !== (truck.payout_email || '') && prev[id] !== undefined) {
              next[id] = prev[id];
            }
          });
          return next;
        });
      }
      if (analyticsRes?.ok) {
        setAnalytics(await analyticsRes.json());
      }
      if (walletRes?.ok) {
        const ws = await walletRes.json();
        setWalletSettings(ws);
        const dollars = (ws.wallet_topup_presets_cents || []).map((c) => c / 100);
        setPresetInput(dollars.join(','));
      }
      if (supportRes?.ok) {
        setSupportSettings(await supportRes.json());
      }
      if (feeRes?.ok) {
        setFeeSettings(await feeRes.json());
      }
      if (reviewsRes?.ok) {
        const data = await reviewsRes.json();
        setReviews(data.reviews || []);
      }
      if (insightsRes?.ok) {
        setInsights(await insightsRes.json());
      }
      if (complaintsRes?.ok) {
        setAdminComplaints(await complaintsRes.json());
      }
      await fetchPayoutData();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Re-fetch reviews when the truck filter changes (without full page loading flash).
    const loadReviews = async () => {
      try {
        const reviewUrl = reviewTruckFilter && reviewTruckFilter !== 'all'
          ? `/api/admin/reviews?truck_id=${encodeURIComponent(reviewTruckFilter)}`
          : '/api/admin/reviews';
        const reviewsRes = await apiFetch(reviewUrl);
        if (reviewsRes.ok) {
          const data = await reviewsRes.json();
          setReviews(data.reviews || []);
        }
      } catch (error) {
        console.error('Failed to filter reviews:', error);
      }
    };
    loadReviews();
  }, [reviewTruckFilter]);

  const handleDeleteReview = async (reviewId) => {
    setDeletingReview(reviewId);
    try {
      const response = await apiFetch(`/api/admin/reviews/${reviewId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete review');
      setReviews((prev) => prev.filter((r) => r.review_id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete review');
    } finally {
      setDeletingReview(null);
    }
  };

  const handleSuppressReview = async (reviewId, suppressed) => {
    setSuppressingReview(reviewId);
    try {
      const response = await apiFetch(`/api/admin/reviews/${reviewId}/suppress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suppressed }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to update review');
      setReviews((prev) =>
        prev.map((r) =>
          r.review_id === reviewId ? { ...r, is_suppressed: suppressed } : r
        )
      );
      toast.success(suppressed ? 'Review suppressed' : 'Review restored');
    } catch (error) {
      toast.error(error.message || 'Failed to update review');
    } finally {
      setSuppressingReview(null);
    }
  };

  const handleToggleActive = async (truck) => {
    const truckId = truck.truck_id;
    const activate = truck.is_active === false;
    setActionLoading(`${truckId}-active`);
    try {
      const response = await apiFetch(
        `/api/admin/trucks/${truckId}/${activate ? 'activate' : 'deactivate'}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update truck');
      }
      toast.success(activate ? 'Truck activated' : 'Truck deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleSponsored = async (truck) => {
    const truckId = truck.truck_id;
    const sponsored = !truck.is_sponsored;
    setActionLoading(`${truckId}-sponsor`);
    try {
      const response = await apiFetch(`/api/admin/trucks/${truckId}/sponsor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sponsored }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update sponsorship');
      }
      toast.success(sponsored ? 'Marked as sponsored' : 'Sponsorship removed');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveWalletSettings = async () => {
    setWalletSaving(true);
    try {
      const presets = presetInput
        .split(',')
        .map((s) => parseFloat(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((d) => Math.round(d * 100));
      if (presets.length === 0) {
        throw new Error('Enter at least one preset amount (e.g. 20,50,100)');
      }
      const response = await apiFetch('/api/admin/wallet-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_bonus_percent: walletSettings.wallet_bonus_percent,
          wallet_topup_presets_cents: presets,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to save');
      setWalletSettings(data);
      const dollars = (data.wallet_topup_presets_cents || []).map((c) => c / 100);
      setPresetInput(dollars.join(','));
      toast.success('Wallet settings saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWalletSaving(false);
    }
  };

  const handleSaveSupportSettings = async () => {
    setSupportSaving(true);
    try {
      const response = await apiFetch('/api/admin/support-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supportSettings),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to save support settings');
      setSupportSettings(data);
      toast.success('Support emails updated');
    } catch (error) {
      toast.error(error.message || 'Failed to save support settings');
    } finally {
      setSupportSaving(false);
    }
  };

  const handleLookupEvidence = async (identifier = evidenceOrderId) => {
    const lookup = String(identifier || '').trim();
    if (!lookup) {
      toast.error('Enter an order number or order ID');
      return;
    }
    setEvidenceOrderId(lookup);
    setEvidenceLoading(true);
    try {
      const response = await apiFetch(`/api/admin/orders/${encodeURIComponent(lookup)}/evidence`);
      const data = await response.json().catch(() => ({}));
      if (response.status === 409) {
        throw new Error(data.detail || 'Multiple orders match — refine your search');
      }
      if (!response.ok) throw new Error(data.detail || 'Order evidence not found');
      setEvidenceData(data);
      requestAnimationFrame(() => {
        document.getElementById('admin-order-lookup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (error) {
      toast.error(error.message || 'Failed to load evidence');
      setEvidenceData(null);
    } finally {
      setEvidenceLoading(false);
    }
  };

  const handleUpdateAdminComplaint = async (complaint, draft) => {
    try {
      const response = await apiFetch(`/api/admin/complaints/${complaint.complaint_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not update complaint');
      setAdminComplaints((prev) => prev.map((item) => (
        item.complaint_id === data.complaint_id ? data : item
      )));
      setEvidenceData((prev) => (prev ? {
        ...prev,
        complaints: (prev.complaints || []).map((item) => (
          item.complaint_id === data.complaint_id ? data : item
        )),
      } : prev));
      toast.success('Complaint updated');
    } catch (error) {
      toast.error(error.message || 'Could not update complaint');
      throw error;
    }
  };

  const handleTruckLocationSaved = (updated) => {
    setLocationTruck(updated);
    setAllTrucks((prev) => prev.map((truck) => (
      truck.truck_id === updated.truck_id ? { ...truck, ...updated } : truck
    )));
    setPendingTrucks((prev) => prev.map((truck) => (
      truck.truck_id === updated.truck_id ? { ...truck, ...updated } : truck
    )));
  };

  const handleSaveCommission = async (truckId) => {
    const value = parseFloat(commissionDrafts[truckId]);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      toast.error('Commission must be between 0 and 100');
      return;
    }
    setCommissionSaving(truckId);
    try {
      const response = await apiFetch(`/api/admin/trucks/${truckId}/commission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendor_commission_percent: value }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to save commission');
      toast.success('Commission updated');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to save commission');
    } finally {
      setCommissionSaving(null);
    }
  };

  const handleSavePayoutEmail = async (truckId) => {
    const email = (payoutEmailDrafts[truckId] || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid Interac e-Transfer email');
      return;
    }
    setPayoutEmailSaving(truckId);
    try {
      const response = await apiFetch(`/api/trucks/${truckId}/payout-email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payout_email: email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to save payout email');
      setAllTrucks((prev) => prev.map((t) => (t.truck_id === truckId ? { ...t, payout_email: data.payout_email } : t)));
      setPayoutEmailDrafts((prev) => ({ ...prev, [truckId]: data.payout_email || email }));
      toast.success('Payout email updated');
    } catch (error) {
      toast.error(error.message || 'Failed to save payout email');
    } finally {
      setPayoutEmailSaving(null);
    }
  };

  const copyTransferAmount = async (amount) => {
    try {
      await navigator.clipboard.writeText(`CA$ ${Number(amount).toFixed(2)}`);
      toast.success('Amount copied');
    } catch {
      toast.error('Could not copy amount');
    }
  };

  const handleSaveFeeSettings = async () => {
    setFeeSaving(true);
    try {
      const response = await apiFetch('/api/admin/fee-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feeSettings),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to save fee settings');
      setFeeSettings(data);
      toast.success('Convenience fee settings updated');
    } catch (error) {
      toast.error(error.message || 'Failed to save fee settings');
    } finally {
      setFeeSaving(false);
    }
  };

  const handleApprove = async (truckId) => {
    setActionLoading(truckId);
    try {
      const response = await apiFetch(`/api/admin/trucks/${truckId}/approve`, { method: 'POST' });

      if (response.ok) {
        toast.success('Truck approved! Vendor has been notified.');
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (truckId) => {
    setActionLoading(truckId);
    try {
      const response = await apiFetch(`/api/admin/trucks/${truckId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason })
      });

      if (response.ok) {
        toast.success('Truck rejected. Vendor has been notified.');
        setRejectingTruck(null);
        setRejectReason('');
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteTruck = async (truckId) => {
    setActionLoading(truckId);
    try {
      const response = await apiFetch(`/api/admin/trucks/${truckId}/delete`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        const message = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || d).join(', ')
            : 'Failed to delete truck';
        throw new Error(message);
      }
      toast.success('Food truck deleted');
      setDeletingTruck(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete truck');
    } finally {
      setActionLoading(null);
    }
  };

  const openStaffModal = async (truck) => {
    setStaffTruck(truck);
    setStaffEmail('');
    setStaffMembers([]);
    setStaffLoading(true);
    try {
      const res = await apiFetch(`/api/admin/trucks/${truck.truck_id}/members`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to load staff');
      setStaffMembers(data.members || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load staff');
      setStaffTruck(null);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleAssignStaff = async () => {
    if (!staffTruck || !staffEmail.trim()) {
      toast.error('Enter the employee email');
      return;
    }
    setStaffAssigning(true);
    try {
      const res = await apiFetch(`/api/admin/trucks/${staffTruck.truck_id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: staffEmail.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to assign');
      }
      toast.success('Staff assigned — they can open the vendor dashboard after signing in');
      setStaffEmail('');
      const refresh = await apiFetch(`/api/admin/trucks/${staffTruck.truck_id}/members`);
      const refreshed = await refresh.json().catch(() => ({}));
      if (refresh.ok) setStaffMembers(refreshed.members || []);
    } catch (error) {
      toast.error(error.message || 'Failed to assign staff');
    } finally {
      setStaffAssigning(false);
    }
  };

  const handleRemoveStaff = async (userId) => {
    if (!staffTruck) return;
    setStaffRemoving(userId);
    try {
      const res = await apiFetch(
        `/api/admin/trucks/${staffTruck.truck_id}/members/${encodeURIComponent(userId)}`,
        { method: 'DELETE' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to remove');
      }
      toast.success('Staff removed');
      setStaffMembers((prev) => prev.filter((m) => m.user_id !== userId));
    } catch (error) {
      toast.error(error.message || 'Failed to remove staff');
    } finally {
      setStaffRemoving(null);
    }
  };

  // Check admin access
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-muted/30">
        <div
          className="bg-card border-b px-6 pb-4"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
        >
          <div className="max-w-7xl mx-auto">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>
        </div>
        <div className="max-w-md mx-auto px-4 pt-24 text-center">
          <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
          <h1 className="text-2xl font-bold mb-4">Admin Access Required</h1>
          <p className="text-muted-foreground">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div
        className="bg-card border-b px-6 pb-4 sticky z-50"
        style={{ top: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <h1 className="font-bold text-lg">Admin Dashboard</h1>
            </div>
          </div>
          <Badge variant="outline" className="gap-1">
            <Users className="w-3 h-3" />
            Admin
          </Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-card rounded-xl p-4 border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-yellow-600">{pendingTrucks.length}</p>
          </div>
          <div className="bg-card rounded-xl p-4 border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs">Approved</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {allTrucks.filter(t => t.is_approved).length}
            </p>
          </div>
          <div className="bg-card rounded-xl p-4 border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Store className="w-4 h-4" />
              <span className="text-xs">Total Trucks</span>
            </div>
            <p className="text-2xl font-bold">{analytics.total_trucks || allTrucks.length}</p>
          </div>
          <div className="bg-card rounded-xl p-4 border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Users className="w-4 h-4" />
              <span className="text-xs">Active Vendors</span>
            </div>
            <p className="text-2xl font-bold">
              {analytics.active_vendors || new Set(allTrucks.filter(t => t.is_approved).map(t => t.vendor_id)).size}
            </p>
          </div>
          <div className="bg-card rounded-xl p-4 border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              CA$ {Number(analytics.total_revenue || 0).toFixed(2)}
            </p>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border mb-6">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Wallet settings
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Top-up bonus (0–5%) applies to all wallet loads. Presets are CAD amounts users can choose on the Wallet page.
          </p>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Bonus %</label>
              <input
                type="number"
                min={0}
                max={5}
                step={0.5}
                value={walletSettings.wallet_bonus_percent}
                onChange={(e) =>
                  setWalletSettings((s) => ({
                    ...s,
                    wallet_bonus_percent: Math.min(5, Math.max(0, parseFloat(e.target.value) || 0)),
                  }))
                }
                className="bg-muted rounded-lg px-3 py-2 text-sm w-24"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">
                Preset amounts (comma-separated dollars)
              </label>
              <input
                type="text"
                value={presetInput}
                onChange={(e) => setPresetInput(e.target.value)}
                placeholder="20,50,100"
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <Button onClick={handleSaveWalletSettings} disabled={walletSaving}>
              {walletSaving ? 'Saving…' : 'Save wallet settings'}
            </Button>
          </div>
        </div>

        <DailyReportSettings />

        <div className="bg-card rounded-xl p-4 border mb-6">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Support contact settings
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            These emails appear on Help and legal pages for students and vendors.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Student support email</label>
              <input
                type="email"
                value={supportSettings.support_email}
                onChange={(e) =>
                  setSupportSettings((s) => ({ ...s, support_email: e.target.value }))
                }
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Vendor support email</label>
              <input
                type="email"
                value={supportSettings.vendor_support_email}
                onChange={(e) =>
                  setSupportSettings((s) => ({ ...s, vendor_support_email: e.target.value }))
                }
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <div className="sm:col-span-2">
              <Button onClick={handleSaveSupportSettings} disabled={supportSaving}>
                {supportSaving ? 'Saving…' : 'Save support settings'}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border mb-6">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Convenience fee settings
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Configure the checkout fee label and formula. The convenience percent applies to food subtotal + combined Taxes & HST (tip excluded). Processing is included inside the Taxes & HST line.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Fee display name</label>
              <input
                type="text"
                value={feeSettings.fee_name}
                onChange={(e) => setFeeSettings((s) => ({ ...s, fee_name: e.target.value }))}
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Percent on subtotal + tax (%)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={feeSettings.convenience_fee_percent}
                onChange={(e) => setFeeSettings((s) => ({ ...s, convenience_fee_percent: Number(e.target.value) || 0 }))}
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Minimum fee (CAD)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={feeSettings.convenience_fee_min_amount}
                onChange={(e) => setFeeSettings((s) => ({ ...s, convenience_fee_min_amount: Number(e.target.value) || 0 }))}
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Free first N orders</label>
              <input
                type="number"
                min={0}
                step={1}
                value={feeSettings.convenience_fee_free_orders_count}
                onChange={(e) => setFeeSettings((s) => ({ ...s, convenience_fee_free_orders_count: Number(e.target.value) || 0 }))}
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Processing inside Taxes & HST (% of subtotal + HST)</label>
              <input
                type="number"
                min={0}
                max={20}
                step={0.1}
                value={feeSettings.processing_percent ?? 2.9}
                onChange={(e) => setFeeSettings((s) => ({ ...s, processing_percent: Number(e.target.value) || 0 }))}
                className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
                data-testid="processing-percent-input"
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between rounded-lg bg-muted px-3 py-3">
              <div>
                <p className="text-sm font-medium">Pause referral rewards</p>
                <p className="text-xs text-muted-foreground">
                  While paused, students cannot redeem referral codes and no CA$1 referral credits are paid out.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={Boolean(feeSettings.referral_paused)}
                  onChange={(e) => setFeeSettings((s) => ({ ...s, referral_paused: e.target.checked }))}
                  className="h-4 w-4"
                  data-testid="referral-paused-toggle"
                />
                Paused
              </label>
            </div>
            <div className="sm:col-span-2">
              <Button onClick={handleSaveFeeSettings} disabled={feeSaving}>
                {feeSaving ? 'Saving…' : 'Save fee settings'}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border mb-6 flex flex-wrap gap-3 items-center">
          <select
            value={viewType}
            onChange={(e) => {
              setViewType(e.target.value);
              setSelectedEntity('all');
            }}
            className="bg-muted rounded-lg px-3 py-2 text-sm"
          >
            <option value="vendors">View by Vendor</option>
            <option value="trucks">View by Food Truck</option>
          </select>

          <select
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
            className="bg-muted rounded-lg px-3 py-2 text-sm min-w-[240px]"
          >
            <option value="all">All {viewType === 'vendors' ? 'Vendors' : 'Food Trucks'}</option>
            {(viewType === 'vendors' ? analytics.vendors : analytics.trucks).map((row) => (
              <option
                key={viewType === 'vendors' ? row.vendor_id : row.truck_id}
                value={viewType === 'vendors' ? row.vendor_id : row.truck_id}
              >
                {viewType === 'vendors' ? row.vendor_name : row.truck_name}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-card rounded-xl p-4 border mb-6" id="admin-order-lookup">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Order dispute evidence lookup
          </h2>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="Order # (e.g. A1B2C3) or full order_id"
              value={evidenceOrderId}
              onChange={(e) => setEvidenceOrderId(e.target.value)}
              className="bg-muted rounded-lg px-3 py-2 text-sm min-w-[220px]"
            />
            <Button onClick={() => handleLookupEvidence()} disabled={evidenceLoading}>
              {evidenceLoading ? 'Loading…' : 'Fetch evidence'}
            </Button>
          </div>
          {evidenceData?.order && (
            <div className="mt-4 border rounded-xl p-4 space-y-5 text-sm" data-testid="admin-order-detail">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="font-semibold mb-2">Order</p>
                  <p><span className="text-muted-foreground">Order #:</span> {formatOrderNumberLabel(evidenceData.order)}</p>
                  <p><span className="text-muted-foreground">Full ID:</span> {evidenceData.order.order_id}</p>
                  <p><span className="text-muted-foreground">Status:</span> {evidenceData.order.status}</p>
                  <p><span className="text-muted-foreground">Pickup PIN:</span> {evidenceData.order.pickup_code || '—'}</p>
                  <p><span className="text-muted-foreground">Scheduled pickup:</span> {evidenceData.order.scheduled_pickup_time || '—'}</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Student</p>
                  <p><span className="text-muted-foreground">Name:</span> {evidenceData.order.student_name || '—'}</p>
                  <p><span className="text-muted-foreground">Email:</span> {evidenceData.order.student_email || '—'}</p>
                  <p><span className="text-muted-foreground">Phone:</span> {evidenceData.order.student_phone || '—'}</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Food truck</p>
                  <p><span className="text-muted-foreground">Truck:</span> {evidenceData.order.truck_name || '—'}</p>
                  <p><span className="text-muted-foreground">Area:</span> {evidenceData.order.truck_location || '—'}</p>
                  <p>
                    <span className="text-muted-foreground">Exact pin:</span>{' '}
                    {evidenceData.order.truck_exact_location
                      ? `${evidenceData.order.truck_exact_location.lat}, ${evidenceData.order.truck_exact_location.lng}${evidenceData.order.truck_exact_location.note ? ` · ${evidenceData.order.truck_exact_location.note}` : ''}`
                      : '—'}
                  </p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-2">Items</p>
                <div className="space-y-2">
                  {(evidenceData.order.items || []).map((item, index) => (
                    <div key={`${item.item_id || item.name}-${index}`} className="flex justify-between gap-3 bg-muted/50 rounded-lg px-3 py-2">
                      <span>
                        {item.quantity} × {item.name}
                        {!!item.modifiers?.length && (
                          <span className="text-muted-foreground"> ({item.modifiers.map((m) => (typeof m === 'string' ? m : m.name)).filter(Boolean).join(', ')})</span>
                        )}
                      </span>
                      <span>CA$ {Number(item.subtotal || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {evidenceData.order.special_instructions && (
                  <p className="mt-2"><span className="text-muted-foreground">Special instructions:</span> {evidenceData.order.special_instructions}</p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <p><span className="text-muted-foreground">Subtotal:</span> CA$ {Number(evidenceData.order.subtotal_amount || 0).toFixed(2)}</p>
                <p>
                  <span className="text-muted-foreground">Taxes & HST:</span> CA$ {Number(evidenceData.order.tax_amount ?? evidenceData.order.tax ?? 0).toFixed(2)}
                  {(evidenceData.order.hst_amount != null || evidenceData.order.processing_amount != null) && (
                    <span className="block text-xs text-muted-foreground">
                      HST CA$ {Number(evidenceData.order.hst_amount || 0).toFixed(2)} · Processing CA$ {Number(evidenceData.order.processing_amount || 0).toFixed(2)}
                    </span>
                  )}
                </p>
                <p><span className="text-muted-foreground">{evidenceData.order.fee_name || 'Convenience fee'}:</span> CA$ {Number(evidenceData.order.platform_fee_amount || 0).toFixed(2)}</p>
                <p><span className="text-muted-foreground">Tip:</span> CA$ {Number(evidenceData.order.tip_amount || 0).toFixed(2)}</p>
                <p><span className="text-muted-foreground">Skip-the-line:</span> CA$ {Number(evidenceData.order.skip_the_line_fee || 0).toFixed(2)}</p>
                <p><span className="text-muted-foreground">Payment:</span> {evidenceData.order.payment_method || '—'}</p>
                <p className="font-medium"><span className="text-muted-foreground">Total:</span> CA$ {Number(evidenceData.order.total_amount || 0).toFixed(2)}</p>
                <p><span className="text-muted-foreground">Refund:</span> {evidenceData.order.refund_id || '—'}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <p><span className="text-muted-foreground">Pickup verified:</span> {evidenceData.order.pickup_verified_at || '—'}</p>
                <p><span className="text-muted-foreground">Confirmed at:</span> {evidenceData.order.confirmed_at || '—'}</p>
                <p><span className="text-muted-foreground">Preparing at:</span> {evidenceData.order.preparing_at || '—'}</p>
                <p><span className="text-muted-foreground">Ready at:</span> {evidenceData.order.ready_at || '—'}</p>
                <p><span className="text-muted-foreground">Picked up at:</span> {evidenceData.order.picked_up_at || '—'}</p>
                <p><span className="text-muted-foreground">Cancelled at:</span> {evidenceData.order.cancelled_at || '—'}</p>
              </div>

              {(evidenceData.complaints || []).length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Complaint history</p>
                  <ComplaintsPanel complaints={evidenceData.complaints} onUpdate={handleUpdateAdminComplaint} testId="admin-order-complaints" />
                </div>
              )}

              <div>
                <p className="font-medium mb-2">Event timeline</p>
                <div className="max-h-48 overflow-auto space-y-2">
                  {(evidenceData.events || []).map((evt) => (
                    <div key={evt.event_id} className="bg-muted/60 rounded-lg p-2">
                      <p className="font-medium">{evt.event_type}</p>
                      <p className="text-xs text-muted-foreground">{evt.created_at}</p>
                      {evt.details && Object.keys(evt.details).length > 0 && (
                        <pre className="text-xs mt-1 overflow-auto">{JSON.stringify(evt.details, null, 2)}</pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-card rounded-xl border overflow-hidden mb-6">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium">{viewType === 'vendors' ? 'Vendor' : 'Truck'}</th>
                <th className="text-left p-4 font-medium">Orders</th>
                <th className="text-left p-4 font-medium">Revenue</th>
                <th className="text-left p-4 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {(viewType === 'vendors' ? analytics.vendors : analytics.trucks)
                .filter((row) => selectedEntity === 'all' || (viewType === 'vendors' ? row.vendor_id : row.truck_id) === selectedEntity)
                .map((row) => (
                  <tr key={viewType === 'vendors' ? row.vendor_id : row.truck_id} className="border-t">
                    <td className="p-4">
                      <div>
                        <p className="font-medium">
                          {viewType === 'vendors' ? row.vendor_name : row.truck_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {viewType === 'vendors' ? row.vendor_email : `Vendor: ${row.vendor_name}`}
                        </p>
                      </div>
                    </td>
                    <td className="p-4 text-sm">{row.orders_count || 0}</td>
                    <td className="p-4 text-sm text-green-700 font-medium">CA$ {Number(row.revenue || 0).toFixed(2)}</td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {viewType === 'vendors'
                        ? `${row.approved_trucks_count || 0}/${row.trucks_count || 0} approved trucks`
                        : (row.is_approved ? 'Approved' : 'Pending')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="w-4 h-4" />
              Pending
              {pendingTrucks.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendingTrucks.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              <Store className="w-4 h-4" />
              All Trucks
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2">
              <Star className="w-4 h-4" />
              Reviews
              {reviews.length > 0 && (
                <Badge variant="secondary" className="ml-1">{reviews.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="complaints" className="gap-2" data-testid="admin-complaints-tab">
              <AlertTriangle className="w-4 h-4" />
              Complaints
              {adminComplaints.filter((c) => c.status === 'open').length > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {adminComplaints.filter((c) => c.status === 'open').length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : pendingTrucks.length === 0 ? (
              <div className="bg-card rounded-xl p-12 text-center border">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h2 className="text-xl font-bold mb-2">All Caught Up!</h2>
                <p className="text-muted-foreground">No pending vendor applications</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {pendingTrucks.map((truck) => (
                  <motion.div
                    key={truck.truck_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-xl border overflow-hidden"
                  >
                    {truck.photo_url && (
                      <img 
                        src={truck.photo_url} 
                        alt={truck.name}
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{truck.name}</h3>
                          <Badge variant="secondary">{truck.cuisine_type || 'Food Truck'}</Badge>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {truck.description}
                      </p>

                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span>{truck.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span>
                            {truck.exact_location
                              ? `Exact pin: ${truck.exact_location.lat}, ${truck.exact_location.lng}${truck.exact_location.note ? ` · ${truck.exact_location.note}` : ''}`
                              : 'No exact pin set'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Utensils className="w-4 h-4" />
                          <span>{truck.menu_items_count || 0} menu items</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          <span>Vendor: {truck.vendor?.name || 'Unknown'}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Applied: {formatDate(truck.created_at)}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(truck.truck_id)}
                          disabled={actionLoading === truck.truck_id}
                          data-testid={`approve-${truck.truck_id}`}
                        >
                          {actionLoading === truck.truck_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => setRejectingTruck(truck)}
                          disabled={actionLoading === truck.truck_id}
                          data-testid={`reject-${truck.truck_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => setLocationTruck(truck)}
                          data-testid={`edit-location-${truck.truck_id}`}
                        >
                          <MapPin className="w-4 h-4 mr-1" />
                          Location
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : allTrucks.length === 0 ? (
              <div className="bg-card rounded-xl p-12 text-center border">
                <Store className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No trucks registered yet</p>
              </div>
            ) : (
              <div className="bg-card rounded-xl border overflow-hidden overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-medium">Truck</th>
                      <th className="text-left p-4 font-medium">Location</th>
                      <th className="text-left p-4 font-medium">Vendor</th>
                      <th className="text-left p-4 font-medium">Commission</th>
                      <th className="text-left p-4 font-medium">Interac payout email</th>
                      <th className="text-left p-4 font-medium">Status</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTrucks.map((truck) => {
                      const isActive = truck.is_active !== false;
                      return (
                        <tr key={truck.truck_id} className="border-t">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {truck.photo_url && (
                                <img
                                  src={truck.photo_url}
                                  alt={truck.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                              )}
                              <div>
                                <p className="font-medium">{truck.name}</p>
                                <p className="text-xs text-muted-foreground">{truck.cuisine_type}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            <p>{truck.location}</p>
                            <p className="text-xs mt-1">
                              {truck.exact_location
                                ? `Pin ${truck.exact_location.lat}, ${truck.exact_location.lng}`
                                : 'No exact pin'}
                            </p>
                          </td>
                          <td className="p-4 text-sm">{truck.vendor?.name || 'Unknown'}</td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                className="w-20 bg-muted rounded px-2 py-1 text-sm"
                                value={commissionDrafts[truck.truck_id] ?? '5'}
                                onChange={(e) => setCommissionDrafts((prev) => ({
                                  ...prev,
                                  [truck.truck_id]: e.target.value,
                                }))}
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={commissionSaving === truck.truck_id}
                                onClick={() => handleSaveCommission(truck.truck_id)}
                              >
                                {commissionSaving === truck.truck_id ? '…' : 'Save'}
                              </Button>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                className={`w-52 bg-muted rounded px-2 py-1 text-sm ${truck.payout_email ? '' : 'ring-1 ring-destructive'}`}
                                placeholder="No payout email"
                                value={payoutEmailDrafts[truck.truck_id] ?? ''}
                                onChange={(e) => setPayoutEmailDrafts((prev) => ({
                                  ...prev,
                                  [truck.truck_id]: e.target.value,
                                }))}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={
                                  payoutEmailSaving === truck.truck_id
                                  || (payoutEmailDrafts[truck.truck_id] ?? '').trim().toLowerCase() === (truck.payout_email || '')
                                }
                                onClick={() => handleSavePayoutEmail(truck.truck_id)}
                              >
                                {payoutEmailSaving === truck.truck_id ? '…' : 'Save'}
                              </Button>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1">
                              {truck.is_approved ? (
                                <Badge className="bg-green-100 text-green-800">Approved</Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                              )}
                              {isActive ? (
                                <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
                              )}
                              {truck.is_sponsored && (
                                <Badge className="bg-orange-100 text-orange-800">Sponsored</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-2">
                              <Link to={`/admin/vendor-dashboard/${truck.vendor_id}`}>
                                <Button variant="outline" size="sm">
                                  View dashboard
                                </Button>
                              </Link>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openStaffModal(truck)}
                                data-testid={`staff-${truck.truck_id}`}
                              >
                                <Users className="w-4 h-4 mr-1" />
                                Staff
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setLocationTruck(truck)}
                                data-testid={`edit-location-${truck.truck_id}`}
                              >
                                <MapPin className="w-4 h-4 mr-1" />
                                Edit location
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={actionLoading === `${truck.truck_id}-active`}
                                onClick={() => handleToggleActive(truck)}
                                data-testid={`toggle-active-${truck.truck_id}`}
                              >
                                {actionLoading === `${truck.truck_id}-active` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Power className="w-4 h-4 mr-1" />
                                    {isActive ? 'Deactivate' : 'Activate'}
                                  </>
                                )}
                              </Button>
                              <Button
                                variant={truck.is_sponsored ? 'default' : 'outline'}
                                size="sm"
                                disabled={actionLoading === `${truck.truck_id}-sponsor`}
                                onClick={() => handleToggleSponsored(truck)}
                                data-testid={`toggle-sponsor-${truck.truck_id}`}
                              >
                                {actionLoading === `${truck.truck_id}-sponsor` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Megaphone className="w-4 h-4 mr-1" />
                                    {truck.is_sponsored ? 'Unsponsor' : 'Sponsor'}
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={actionLoading === truck.truck_id}
                                onClick={() => setDeletingTruck(truck)}
                                data-testid={`delete-truck-${truck.truck_id}`}
                              >
                                {actionLoading === truck.truck_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Delete
                                  </>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="flex flex-wrap gap-2">
              {PAYOUT_PERIODS.map((p) => (
                <Button
                  key={p.id}
                  size="sm"
                  variant={payoutPeriod === p.id ? 'default' : 'outline'}
                  onClick={() => setPayoutPeriod(p.id)}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            <AdminProfitSection period={payoutPeriod} />

            {payoutSummary && (
              <div className="space-y-4">
                <div className="bg-card rounded-xl p-6 border">
                  <p className="text-sm text-muted-foreground mb-1">Total to transfer ({payoutPeriod})</p>
                  <p className="text-4xl font-bold text-green-700">
                    CA$ {(payoutSummary.transfer_amount || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {payoutSummary.orders_fulfilled || 0} fulfilled pickups · commission CA$ {(payoutSummary.commission_total || 0).toFixed(2)}
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {(payoutSummary.trucks || []).map((row) => (
                    <div key={row.truck_id} className="bg-card rounded-xl p-4 border">
                      <p className="font-semibold">{row.truck_name}</p>
                      <p className="text-2xl font-bold text-green-700 mt-2">
                        CA$ {(row.transfer_amount || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {row.orders_fulfilled || 0} fulfilled · {row.commission_percent ?? 5}% commission
                      </p>
                      <p className={`text-xs mt-1 break-all ${row.payout_email ? 'text-muted-foreground' : 'text-destructive'}`}>
                        {row.payout_email ? `Interac: ${row.payout_email}` : 'No payout email on file'}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => copyTransferAmount(row.transfer_amount || 0)}
                      >
                        Copy amount
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="bg-card rounded-xl p-4 border overflow-x-auto">
                  <h3 className="font-semibold mb-3">Order drill-down</h3>
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3">Order</th>
                        <th className="text-left p-3">Pickup</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Gross</th>
                        <th className="text-left p-3">Tax</th>
                        <th className="text-left p-3">Commission</th>
                        <th className="text-left p-3">Transfer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutOrders.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="p-4 text-muted-foreground">No orders in this period.</td>
                        </tr>
                      ) : (
                        payoutOrders.map((row) => (
                          <tr key={row.order_id} className="border-t">
                            <td className="p-3">
                              <button
                                type="button"
                                className="font-medium text-primary hover:underline"
                                onClick={() => handleLookupEvidence(row.order_id)}
                                data-testid={`admin-order-detail-${row.order_id}`}
                              >
                                {formatOrderNumberLabel(row)}
                              </button>
                              <p className="text-xs text-muted-foreground">{row.items_summary}</p>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              {row.scheduled_pickup_time
                                ? new Date(row.scheduled_pickup_time).toLocaleString('en-CA', {
                                  timeZone: 'America/Toronto',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })
                                : '—'}
                            </td>
                            <td className="p-3 capitalize">{row.status?.replace(/_/g, ' ')}</td>
                            <td className="p-3">CA$ {(row.gross_food || 0).toFixed(2)}</td>
                            <td className="p-3">CA$ {(row.tax_component || 0).toFixed(2)}</td>
                            <td className="p-3">CA$ {(row.commission || 0).toFixed(2)}</td>
                            <td className="p-3 font-medium text-green-700">CA$ {(row.transfer_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!insights ? (
              <div className="bg-card rounded-xl p-8 text-center border text-muted-foreground">
                {loading ? 'Loading analytics…' : 'No analytics data yet'}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {[
                    { label: 'Orders Today', value: insights.orders_today, icon: Store },
                    { label: 'Orders Week', value: insights.orders_week, icon: Store },
                    { label: 'Orders Month', value: insights.orders_month, icon: Store },
                    { label: 'Revenue', value: `CA$ ${(insights.total_revenue || 0).toFixed(2)}`, icon: DollarSign },
                    { label: 'Avg Rating', value: insights.rating_count ? `${Number(insights.rating_avg).toFixed(1)} (${insights.rating_count})` : '—', icon: Star },
                    {
                      label: 'Avg Ready Delay',
                      value: insights.avg_ready_delay_minutes == null
                        ? '—'
                        : `${insights.avg_ready_delay_minutes > 0 ? '+' : ''}${insights.avg_ready_delay_minutes} min`,
                      icon: Clock,
                    },
                  ].map((kpi) => (
                    <div key={kpi.label} className="bg-card rounded-xl p-4 border">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <kpi.icon className="w-4 h-4" />
                        <span className="text-xs">{kpi.label}</span>
                      </div>
                      <p className="text-xl font-bold">{kpi.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-card rounded-xl p-4 border">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Orders & revenue — last 30 days
                    </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={insights.revenue_last_30_days || []}>
                          <defs>
                            <linearGradient id="adminRev" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#E53E3E" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#E53E3E" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => (d ? d.slice(5) : '')} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(v, name) =>
                              name === 'revenue' ? [`CA$ ${Number(v).toFixed(2)}`, 'Revenue'] : [v, 'Orders']
                            }
                          />
                          <Area type="monotone" dataKey="revenue" stroke="#E53E3E" fill="url(#adminRev)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-4 border">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Peak order hours
                    </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={insights.peak_hours || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}:00`} interval={1} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [v, 'Orders']} labelFormatter={(h) => `${h}:00`} />
                          <Bar dataKey="count" fill="#E53E3E" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-xl border overflow-hidden overflow-x-auto">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold">All food trucks</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Delay = ready time minus scheduled pickup (positive = late). Only orders with a recorded ready time are included.
                    </p>
                  </div>
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium text-sm">Truck</th>
                        <th className="text-left p-3 font-medium text-sm">Orders</th>
                        <th className="text-left p-3 font-medium text-sm">Revenue</th>
                        <th className="text-left p-3 font-medium text-sm">Rating</th>
                        <th className="text-left p-3 font-medium text-sm">Avg delay</th>
                        <th className="text-left p-3 font-medium text-sm">Samples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(insights.trucks || []).map((row) => (
                        <tr key={row.truck_id} className="border-t">
                          <td className="p-3 text-sm font-medium">
                            {row.truck_name}
                            {!row.is_active && (
                              <Badge className="ml-2 bg-gray-100 text-gray-800 text-xs">Inactive</Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm">
                            {row.orders_all}
                            <span className="text-xs text-muted-foreground ml-1">
                              (t {row.orders_today} / w {row.orders_week} / m {row.orders_month})
                            </span>
                          </td>
                          <td className="p-3 text-sm text-green-700">CA$ {(row.revenue || 0).toFixed(2)}</td>
                          <td className="p-3 text-sm">
                            {row.rating_count
                              ? `${Number(row.rating_avg).toFixed(1)} (${row.rating_count})`
                              : '—'}
                          </td>
                          <td className="p-3 text-sm">
                            {row.avg_ready_delay_minutes == null
                              ? '—'
                              : `${row.avg_ready_delay_minutes > 0 ? '+' : ''}${row.avg_ready_delay_minutes} min`}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{row.delay_sample_size || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm text-muted-foreground">Filter by truck</label>
              <select
                value={reviewTruckFilter}
                onChange={(e) => setReviewTruckFilter(e.target.value)}
                className="rounded-lg border bg-background px-3 py-2 text-sm"
                data-testid="review-truck-filter"
              >
                <option value="all">All trucks</option>
                {allTrucks.map((t) => (
                  <option key={t.truck_id} value={t.truck_id}>{t.name}</option>
                ))}
              </select>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="bg-card rounded-xl p-12 text-center border">
                <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No reviews yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div
                    key={review.review_id}
                    className={`bg-card rounded-xl p-4 border ${review.is_suppressed ? 'opacity-70' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-sm">{review.student_name || 'Student'}</span>
                          {review.truck_name && (
                            <Badge variant="secondary" className="text-xs">{review.truck_name}</Badge>
                          )}
                          {review.is_suppressed && (
                            <Badge className="bg-gray-100 text-gray-800 text-xs">Suppressed</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${review.rating >= star ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
                            />
                          ))}
                        </div>
                        {review.review_text && (
                          <p className="text-sm text-muted-foreground">{review.review_text}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={suppressingReview === review.review_id}
                          onClick={() => handleSuppressReview(review.review_id, !review.is_suppressed)}
                          data-testid={`suppress-review-${review.review_id}`}
                        >
                          {suppressingReview === review.review_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : review.is_suppressed ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          disabled={deletingReview === review.review_id}
                          onClick={() => handleDeleteReview(review.review_id)}
                          data-testid={`delete-review-${review.review_id}`}
                        >
                          {deletingReview === review.review_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="complaints" className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <select
                value={complaintStatusFilter}
                onChange={(e) => setComplaintStatusFilter(e.target.value)}
                className="bg-muted rounded-lg px-3 py-2 text-sm"
                data-testid="admin-complaint-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
              <select
                value={complaintTruckFilter}
                onChange={(e) => setComplaintTruckFilter(e.target.value)}
                className="bg-muted rounded-lg px-3 py-2 text-sm"
                data-testid="admin-complaint-truck-filter"
              >
                <option value="all">All trucks</option>
                {allTrucks.map((truck) => (
                  <option key={truck.truck_id} value={truck.truck_id}>{truck.name}</option>
                ))}
              </select>
            </div>
            <ComplaintsPanel
              complaints={adminComplaints.filter((complaint) => (
                (complaintStatusFilter === 'all' || complaint.status === complaintStatusFilter)
                && (complaintTruckFilter === 'all' || complaint.truck_id === complaintTruckFilter)
              ))}
              onUpdate={handleUpdateAdminComplaint}
              showTruck
              testId="admin-complaints-panel"
            />
          </TabsContent>
        </Tabs>
      </div>

      {locationTruck && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-location-modal">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl bg-background p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-lg">Edit truck location</h2>
                <p className="text-sm text-muted-foreground">{locationTruck.name}</p>
              </div>
              <Button variant="outline" onClick={() => setLocationTruck(null)}>Close</Button>
            </div>
            <TruckLocationSettings truck={locationTruck} onSaved={handleTruckLocationSaved} />
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectingTruck && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="font-bold text-lg">Reject Application</h2>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to reject <strong>{rejectingTruck.name}</strong>? 
                This will delete the truck and notify the vendor.
              </p>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Reason (optional)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Incomplete menu, missing photos..."
                  className="w-full p-3 rounded-lg bg-muted border-0 resize-none text-sm"
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setRejectingTruck(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={() => handleReject(rejectingTruck.truck_id)}
                  disabled={actionLoading === rejectingTruck.truck_id}
                >
                  {actionLoading === rejectingTruck.truck_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Reject & Notify'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete truck modal */}
      {deletingTruck && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="font-bold text-lg text-destructive flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Delete food truck
              </h2>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Permanently delete <strong>{deletingTruck.name}</strong>? This removes the truck and its menu.
                Order history is kept. This cannot be undone.
              </p>
              <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
                Deletion is blocked if the truck has active orders (awaiting pickup).
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeletingTruck(null)}
                  disabled={actionLoading === deletingTruck.truck_id}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleDeleteTruck(deletingTruck.truck_id)}
                  disabled={actionLoading === deletingTruck.truck_id}
                >
                  {actionLoading === deletingTruck.truck_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Delete permanently'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {staffTruck && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="p-4 border-b flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Truck staff
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {staffTruck.name} — assign employees who already signed up
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStaffTruck(null);
                  setStaffMembers([]);
                  setStaffEmail('');
                }}
              >
                Close
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="employee@email.com"
                  value={staffEmail}
                  onChange={(e) => setStaffEmail(e.target.value)}
                  className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm"
                  data-testid="staff-email-input"
                />
                <Button
                  onClick={handleAssignStaff}
                  disabled={staffAssigning || !staffEmail.trim()}
                  data-testid="assign-staff-btn"
                >
                  {staffAssigning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-1" />
                      Assign
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Person must create an account first. Assignment promotes them to vendor so they can open the dashboard for this truck.
              </p>

              {staffLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : staffMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No members yet</p>
              ) : (
                <ul className="space-y-2">
                  {staffMembers.map((m) => (
                    <li
                      key={m.user_id}
                      className="flex items-center justify-between gap-3 rounded-xl border p-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{m.name || 'Unnamed'}</p>
                        <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                        <Badge
                          variant="secondary"
                          className="mt-1 capitalize"
                        >
                          {m.membership_role}
                        </Badge>
                      </div>
                      {m.can_remove ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={staffRemoving === m.user_id}
                          onClick={() => handleRemoveStaff(m.user_id)}
                        >
                          {staffRemoving === m.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Remove'
                          )}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground shrink-0">Owner</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
